/* ============================================================
   Prestige — original theme for Prestige Truck
   Shared behaviors. Vanilla JS, no dependencies.
   ============================================================ */
(function () {
  'use strict';

  var theme = window.theme || { routes: {}, settings: {}, strings: {} };

  /* ---------- Helpers ---------- */

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || theme.moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function announce(message) {
    var region = document.querySelector('[data-live-region]');
    if (region) {
      region.textContent = '';
      window.setTimeout(function () { region.textContent = message; }, 50);
    }
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str == null ? '' : String(str)));
    return div.innerHTML;
  }

  /* ---------- Sticky header state ---------- */

  function initStickyHeader() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle('site-header--scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Replace the Liquid-estimated --header-height with the real rendered
    // height (the estimate assumes the logo reaches its max height), so
    // sticky offsets below the header sit exactly 24px under it.
    var setHeaderHeight = function () {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    };
    setHeaderHeight();
    window.addEventListener('resize', debounce(setHeaderHeight, 150));
  }

  /* ---------- Slide-out header search ---------- */

  function initSearchSlideout() {
    var OPEN_CLASS = 'search-open';

    // Single search toggle in the header icons; the helpers tolerate
    // multiple [data-search-toggle] nodes for safety.
    function allToggles() {
      return document.querySelectorAll('[data-search-toggle]');
    }
    function visibleToggle() {
      var found = null;
      allToggles().forEach(function (t) {
        if (!found && t.offsetParent !== null) found = t;
      });
      return found;
    }
    function setExpanded(value) {
      allToggles().forEach(function (t) { t.setAttribute('aria-expanded', value); });
    }
    function isOpen() {
      return document.body.classList.contains(OPEN_CLASS);
    }
    function open() {
      document.body.classList.add(OPEN_CLASS);
      setExpanded('true');
      // Focus lands mid-reveal, not at the end: focusing immediately would
      // scroll/jump before the bar is on screen. Paired with the 380ms
      // wipe in header.liquid.
      var input = document.querySelector('[data-header-search] [data-search-input]');
      if (input) window.setTimeout(function () { input.focus(); }, 200);
    }
    function close() {
      document.body.classList.remove(OPEN_CLASS);
      setExpanded('false');
      closePredictive();
    }

    document.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;
      var toggle = e.target.closest('[data-search-toggle]');
      if (toggle) {
        e.preventDefault();
        if (isOpen()) { close(); } else { open(); }
        return;
      }
      if (!e.target.closest('[data-header-search]')) {
        if (isOpen()) {
          close();
        } else {
          // Always-visible bar mode: no search-open class, but the
          // suggestions panel still needs outside-click dismissal.
          closePredictive();
        }
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (isOpen()) {
        close();
        var t = visibleToggle();
        if (t) t.focus();
      } else if (predictivePanel && !predictivePanel.hidden) {
        closePredictive();
      }
    });
  }

  /* ---------- Predictive search ---------- */

  var predictivePanel = null;
  var predictiveInput = null;
  var predictiveStatus = null;
  var predictiveIdleHTML = '';
  var predictiveController = null;
  var predictiveSeq = 0;
  var predictiveActive = -1;
  var predictiveTypedTerm = '';
  var RECENT_KEY = 'theme:recent-searches';
  var RECENT_MAX = 5;

  // Repeat queries are common (backspacing, retyping). The endpoint sends no
  // Cache-Control at all, so the browser will not cache for us. Capped and
  // time-limited so it cannot serve stale prices after an inventory change.
  var predictiveCache = new Map();
  var CACHE_MAX = 50;
  var CACHE_TTL = 5 * 60 * 1000;

  function cacheGet(key) {
    var hit = predictiveCache.get(key);
    if (!hit) return null;
    if (Date.now() - hit.at > CACHE_TTL) { predictiveCache.delete(key); return null; }
    // Refresh recency for LRU eviction.
    predictiveCache.delete(key);
    predictiveCache.set(key, hit);
    return hit.html;
  }

  function cacheSet(key, html) {
    predictiveCache.set(key, { html: html, at: Date.now() });
    if (predictiveCache.size > CACHE_MAX) {
      predictiveCache.delete(predictiveCache.keys().next().value);
    }
  }

  /* --- Recent searches (this browser only, never sent anywhere) --- */

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];
      // Drop anything that is not a usable term and write the cleaned list back,
      // so a browser that already holds a bad entry heals itself on first open.
      // One non-string entry used to render as "[object Object]" AND throw out of
      // pushRecent's filter, which silently stopped every later write.
      var clean = list.filter(function (term) {
        return typeof term === 'string' &&
          term.trim() !== '' &&
          term.trim().toLowerCase() !== '[object object]';
      }).slice(0, RECENT_MAX);
      if (clean.length !== list.length) {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(clean));
      }
      return clean;
    } catch (e) { return []; }
  }

  function pushRecent(term) {
    term = typeof term === 'string' ? term.trim() : '';
    if (!term) return;
    try {
      var list = readRecent().filter(function (t) {
        return t.toLowerCase() !== term.toLowerCase();
      });
      list.unshift(term);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* private mode, quota - not worth surfacing */ }
  }

  function renderRecent() {
    if (!predictivePanel) return;
    var wrap = predictivePanel.querySelector('[data-search-recent]');
    var list = predictivePanel.querySelector('[data-search-recent-list]');
    if (!wrap || !list) return;
    var items = readRecent();
    if (!items.length) { wrap.hidden = true; list.innerHTML = ''; return; }
    var html = '';
    items.forEach(function (term, i) {
      html += '<li><a class="search-panel__chip" role="option" aria-selected="false"' +
        ' id="search-opt-recent-' + i + '" tabindex="-1" data-search-option' +
        ' data-search-term="' + escapeHtml(term) + '"' +
        ' href="' + theme.routes.search + '?q=' + encodeURIComponent(term) + '">' +
        escapeHtml(term) + '</a></li>';
    });
    list.innerHTML = html;
    wrap.hidden = false;
  }

  /* --- Panel state --- */

  function predictiveOptions() {
    return predictivePanel ? Array.prototype.slice.call(
      predictivePanel.querySelectorAll('[data-search-option]')
    ) : [];
  }

  function setActive(index) {
    var opts = predictiveOptions();
    opts.forEach(function (o) { o.setAttribute('aria-selected', 'false'); o.classList.remove('is-active'); });
    predictiveActive = index;
    if (index < 0 || index >= opts.length) {
      if (predictiveInput) predictiveInput.setAttribute('aria-activedescendant', '');
      return;
    }
    var el = opts[index];
    el.setAttribute('aria-selected', 'true');
    el.classList.add('is-active');
    if (predictiveInput) predictiveInput.setAttribute('aria-activedescendant', el.id || '');
    // Without this, arrowing walks into options below the fold.
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    // Copy the term into the field so autocomplete works as a query BUILDER,
    // not just a submit shortcut - the shopper can arrow to "torque wrench"
    // and keep typing " 1/2".
    var term = el.getAttribute('data-search-term');
    if (term && predictiveInput) predictiveInput.value = term;
  }

  function openPredictive() {
    if (!predictivePanel) return;
    predictivePanel.hidden = false;
    if (predictiveInput) predictiveInput.setAttribute('aria-expanded', 'true');
  }

  function closePredictive() {
    predictiveSeq += 1;
    if (predictiveController) { predictiveController.abort(); predictiveController = null; }
    if (predictivePanel) predictivePanel.hidden = true;
    if (predictiveInput) {
      predictiveInput.setAttribute('aria-expanded', 'false');
      predictiveInput.setAttribute('aria-activedescendant', '');
    }
    predictiveActive = -1;
  }

  function setPanelHTML(html) {
    if (!predictivePanel) return;
    predictivePanel.innerHTML = html;
    predictiveActive = -1;
    if (predictiveInput) predictiveInput.setAttribute('aria-activedescendant', '');
  }

  function showIdle() {
    setPanelHTML(predictiveIdleHTML);
    renderRecent();
    var hasAnything = predictivePanel && predictivePanel.querySelector('[data-search-option]');
    if (hasAnything) { openPredictive(); } else { closePredictive(); }
  }

  function announceResults(terms) {
    if (!predictiveStatus) return;
    var opts = predictiveOptions().length;
    predictiveStatus.textContent = opts
      ? (theme.strings.resultsWithCount || '').replace('__COUNT__', opts).replace('__TERMS__', terms) ||
        opts + ' results for ' + terms
      : (theme.strings.noResults || 'No results found for __TERMS__').replace('__TERMS__', terms);
  }

  function initPredictiveSearch() {
    if (theme.settings.predictiveSearch === false) return;
    var wrap = document.querySelector('[data-header-search]');
    if (!wrap) return;
    predictiveInput = wrap.querySelector('[data-search-input]');
    predictivePanel = wrap.querySelector('[data-search-suggest]');
    predictiveStatus = wrap.querySelector('[data-search-status]');
    if (!predictiveInput || !predictivePanel) return;

    var idleTemplate = wrap.querySelector('[data-search-idle-template]');
    predictiveIdleHTML = idleTemplate ? idleTemplate.innerHTML : predictivePanel.innerHTML;

    var minChars = parseInt(theme.settings.searchMinChars, 10);
    if (isNaN(minChars) || minChars < 1) minChars = 1;

    function fetchFor(terms) {
      var cached = cacheGet(terms);
      if (cached != null) { setPanelHTML(cached); openPredictive(); announceResults(terms); return; }

      // Abort the previous request AND guard on a sequence number. The abort
      // alone leaves a window where a slower earlier response is already in
      // the network stack and would overwrite newer results.
      if (predictiveController) predictiveController.abort();
      predictiveController = new AbortController();
      var seq = ++predictiveSeq;

      // limit_scope=each is essential: the default caps the whole response at
      // 10 results ACROSS all types, so products crowd out everything else.
      var url = theme.routes.predictiveSearch +
        '?q=' + encodeURIComponent(terms) +
        '&resources[type]=query,product,collection,page' +
        '&resources[limit]=6' +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        '&section_id=predictive-search';

      fetch(url, { signal: predictiveController.signal })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (text) {
          if (seq !== predictiveSeq) return;
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var inner = doc.querySelector('[data-search-panel-inner]');
          setPanelHTML(inner ? inner.outerHTML : '');
          openPredictive();
          announceResults(terms);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // Never strand the shopper on a network blip - fall back to the
          // idle panel rather than an empty box.
          if (seq === predictiveSeq) showIdle();
        });
    }

    var debouncedFetch = debounce(function (terms) { fetchFor(terms); }, 200);

    predictiveInput.addEventListener('input', function () {
      var terms = predictiveInput.value.trim();
      predictiveTypedTerm = terms;
      if (terms.length < minChars) {
        if (predictiveController) { predictiveController.abort(); predictiveController = null; }
        predictiveSeq += 1;
        showIdle();
        return;
      }
      debouncedFetch(terms);
    });

    predictiveInput.addEventListener('focus', function () {
      var terms = predictiveInput.value.trim();
      if (terms.length >= minChars) { fetchFor(terms); } else { showIdle(); }
    });

    predictiveInput.addEventListener('keydown', function (e) {
      var opts = predictiveOptions();
      var open = !predictivePanel.hidden && opts.length > 0;

      if (e.key === 'ArrowDown') {
        if (!open) { showIdle(); return; }
        e.preventDefault();
        setActive(predictiveActive + 1 >= opts.length ? 0 : predictiveActive + 1);
      } else if (e.key === 'ArrowUp') {
        if (!open) return;
        e.preventDefault();
        if (predictiveActive <= 0) {
          // Back to the field with whatever they actually typed restored.
          setActive(-1);
          predictiveInput.value = predictiveTypedTerm;
        } else {
          setActive(predictiveActive - 1);
        }
      } else if (e.key === 'Home' && open) {
        e.preventDefault(); setActive(0);
      } else if (e.key === 'End' && open) {
        e.preventDefault(); setActive(opts.length - 1);
      } else if (e.key === 'Enter') {
        if (open && predictiveActive >= 0) {
          e.preventDefault();
          pushRecent(predictiveInput.value.trim());
          window.location.href = opts[predictiveActive].getAttribute('href');
        } else {
          pushRecent(predictiveInput.value.trim());
        }
      } else if (e.key === 'Escape') {
        // First press closes and keeps the text; a second clears the field.
        if (!predictivePanel.hidden) {
          e.preventDefault();
          closePredictive();
        } else if (predictiveInput.value) {
          e.preventDefault();
          predictiveInput.value = '';
          predictiveTypedTerm = '';
          showIdle();
        }
      }
    });

    // Delegated so it survives every panel swap.
    predictivePanel.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;

      var fill = e.target.closest('[data-search-fill]');
      if (fill) {
        // Build on the suggestion instead of submitting it.
        e.preventDefault();
        e.stopPropagation();
        predictiveInput.value = fill.getAttribute('data-search-fill');
        predictiveTypedTerm = predictiveInput.value;
        predictiveInput.focus();
        fetchFor(predictiveInput.value.trim());
        return;
      }

      if (e.target.closest('[data-search-clear-recent]')) {
        e.preventDefault();
        try { window.localStorage.removeItem(RECENT_KEY); } catch (err) {}
        renderRecent();
        return;
      }

      var opt = e.target.closest('[data-search-option]');
      if (opt) pushRecent(predictiveInput.value.trim());
    });

    var form = predictiveInput.form;
    if (form) {
      form.addEventListener('submit', function () {
        pushRecent(predictiveInput.value.trim());
      });
    }
  }

  /* ---------- Mobile navigation drawer ---------- */

  function initMobileNav() {
    // Delegated, with the drawer/button re-queried per interaction, so the
    // drawer keeps working after the theme editor re-renders the header
    // section (which replaces the drawer and toggle nodes wholesale).
    function setOpen(open) {
      var drawer = document.querySelector('[data-mobile-nav]');
      var openBtn = document.querySelector('[data-mobile-nav-open]');
      if (!drawer) return;
      document.body.classList.toggle('mobile-nav-open', open);
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (openBtn) openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        var firstLink = drawer.querySelector('a, button');
        if (firstLink) firstLink.focus();
      } else if (drawer.contains(document.activeElement) && openBtn) {
        // Don't strand focus inside the aria-hidden drawer on any close path.
        openBtn.focus();
      }
    }

    document.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('[data-mobile-nav-open]')) {
        setOpen(!document.body.classList.contains('mobile-nav-open'));
      } else if (
        e.target.closest('[data-mobile-nav-close]') ||
        e.target.closest('[data-mobile-nav-overlay]')
      ) {
        setOpen(false);
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('mobile-nav-open')) {
        setOpen(false);
        var openBtn = document.querySelector('[data-mobile-nav-open]');
        if (openBtn) openBtn.focus();
      }
    });
  }

  /* ---------- Desktop dropdown menus (keyboard support) ---------- */

  function initDesktopNav() {
    // Delegated (not bound per-button) so the toggles keep working after
    // the theme editor re-renders the header section with fresh markup.
    document.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;
      var btn = e.target.closest('[data-nav-item-toggle]');
      if (btn) {
        var li = btn.closest('[data-nav-item]');
        if (!li) return;
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        document.querySelectorAll('[data-nav-item].is-open').forEach(function (other) {
          if (other !== li) {
            other.classList.remove('is-open');
            var b = other.querySelector('[data-nav-item-toggle]');
            if (b) b.setAttribute('aria-expanded', 'false');
          }
        });
        li.classList.toggle('is-open', !expanded);
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        return;
      }
      if (!e.target.closest('[data-nav-item]')) {
        document.querySelectorAll('[data-nav-item].is-open').forEach(function (item) {
          item.classList.remove('is-open');
          var b = item.querySelector('[data-nav-item-toggle]');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  /* ---------- Quantity steppers (delegated) ---------- */

  function initQuantity() {
    document.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;
      var btn = e.target.closest('[data-qty-change]');
      if (!btn) return;
      var qtyWrap = btn.closest('.qty');
      var input = qtyWrap && qtyWrap.querySelector('.qty__input');
      if (!input) return;
      var step = btn.getAttribute('data-qty-change') === 'up' ? 1 : -1;
      var min = parseInt(input.min || '1', 10);
      var next = (parseInt(input.value, 10) || min) + step;
      if (next < min) next = min;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Cart count ---------- */

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count > 0 ? count : '';
      el.classList.toggle('is-visible', count > 0);
    });
  }

  function refreshCartCount() {
    fetch(theme.routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCartCount(cart.item_count); })
      .catch(function () {});
  }

  /* ---------- Cart drawer ---------- */

  var cartBusy = false;

  function drawerEl() {
    return document.querySelector('[data-cart-drawer]');
  }

  function openCartDrawer() {
    var drawer = drawerEl();
    if (!drawer) return;
    document.body.classList.add('cart-open');
    drawer.setAttribute('aria-hidden', 'false');
    var closeBtn = drawer.querySelector('[data-cart-drawer-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeCartDrawer() {
    var drawer = drawerEl();
    if (!drawer) return;
    document.body.classList.remove('cart-open');
    drawer.setAttribute('aria-hidden', 'true');
  }

  // Re-render the drawer from the Section Rendering API so it always
  // reflects the live cart, then optionally slide it open.
  function refreshCartDrawer(openAfter) {
    var drawer = drawerEl();
    if (!drawer) return Promise.resolve();
    return fetch(theme.routes.root + '?sections=cart-drawer', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-content]');
        var current = drawer.querySelector('[data-cart-drawer-content]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        if (openAfter) openCartDrawer();
      })
      .catch(function () {});
  }

  function changeCartLine(key, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    var drawer = drawerEl();
    if (drawer) drawer.classList.add('cart-drawer--busy');
    fetch(theme.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        return refreshCartDrawer(false);
      })
      .catch(function () {})
      .then(function () {
        cartBusy = false;
        if (drawer) drawer.classList.remove('cart-drawer--busy');
      });
  }

  function initCartDrawer() {
    if (!drawerEl()) return;

    // Arriving from /cart with cart_drawer_only on: that page redirects here
    // and leaves a flag so the drawer opens instead of the shopper landing
    // somewhere with no sign their cart was reached.
    try {
      if (sessionStorage.getItem('theme:open-cart-drawer')) {
        sessionStorage.removeItem('theme:open-cart-drawer');
        refreshCartDrawer(true);
      }
    } catch (e) {}

    document.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;

      var opener = e.target.closest('[data-cart-drawer-open]');
      if (opener) {
        e.preventDefault();
        refreshCartDrawer(true);
        return;
      }

      if (e.target.closest('[data-cart-drawer-close]') || e.target.closest('[data-cart-drawer-overlay]')) {
        closeCartDrawer();
        return;
      }

      var removeBtn = e.target.closest('[data-cart-line-remove]');
      if (removeBtn) {
        changeCartLine(removeBtn.getAttribute('data-line-key'), 0);
      }
    });

    document.addEventListener('change', function (e) {
      if (!(e.target instanceof Element)) return;
      if (!e.target.matches('[data-cart-line-input]')) return;
      var qty = parseInt(e.target.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      changeCartLine(e.target.getAttribute('data-line-key'), qty);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('cart-open')) {
        closeCartDrawer();
      }
    });
  }

  /* ---------- AJAX add to cart ---------- */

  function initAjaxAtc() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('[data-ajax-atc]')) return;
      e.preventDefault();

      var button = form.querySelector('[type="submit"]');
      var originalText = button ? button.textContent : '';
      if (button) {
        button.disabled = true;
        button.textContent = theme.strings.adding || 'Adding…';
      }

      fetch(theme.routes.cartAdd + '.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (data) { return Promise.reject(data); });
          return r.json();
        })
        .then(function () {
          announce(theme.strings.added || 'Added to cart');
          refreshCartCount();
          // Slide the cart drawer open with the fresh contents
          refreshCartDrawer(true);
          if (button) {
            button.textContent = originalText;
            button.disabled = false;
          }
        })
        .catch(function (err) {
          if (button) {
            button.textContent = originalText;
            button.disabled = false;
          }
          var msg = (err && (err.description || err.message)) || 'Could not add to cart';
          announce(msg);
          window.alert(msg);
        });
    });
  }

  /* ---------- Back to top ---------- */

  function initBackToTop() {
    var btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    var onScroll = function () {
      btn.hidden = window.scrollY < 600;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ---------- Carousel arrows ---------- */

  function initCarousels(root) {
    (root || document).querySelectorAll('[data-carousel]').forEach(function (carousel) {
      if (carousel.dataset.carouselBound === 'true') return;
      carousel.dataset.carouselBound = 'true';
      var track = carousel.querySelector('.carousel__track');
      var prev = carousel.querySelector('.carousel__arrow--prev');
      var next = carousel.querySelector('.carousel__arrow--next');
      if (!track) return;

      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }

      function page(direction) {
        track.scrollBy({ left: direction * track.clientWidth * 0.85, behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });
      track.addEventListener('scroll', debounce(update, 80), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ---------- Recently viewed (localStorage) ---------- */

  var RECENT_KEY = 'prestige:recently-viewed';

  function trackRecentlyViewed() {
    if (theme.settings.recentlyViewed === false) return;
    var el = document.querySelector('[data-product-json]');
    if (!el) return;
    var data;
    try {
      data = JSON.parse(el.textContent);
    } catch (err) {
      return;
    }
    if (!data || !data.handle) return;
    var list;
    try {
      list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
    } catch (err) {
      list = [];
    }
    list = list.filter(function (item) { return item.handle !== data.handle; });
    list.unshift(data);
    list = list.slice(0, 12);
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (err) { /* storage full/blocked */ }
  }

  function renderRecentlyViewed() {
    var container = document.querySelector('[data-recently-viewed]');
    if (!container) return;
    var excludeHandle = container.getAttribute('data-exclude-handle') || '';
    var list;
    try {
      list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
    } catch (err) {
      list = [];
    }
    list = list.filter(function (item) { return item.handle !== excludeHandle; }).slice(0, 8);
    if (!list.length) return;

    var emphasize = theme.settings.emphasizePrice !== false;
    var html = list.map(function (item) {
      return (
        '<div class="product-card' + (emphasize ? ' product-card--emphasize-price' : '') + '">' +
        '<a class="product-card__media" href="' + item.url + '" tabindex="-1" aria-hidden="true">' +
        (item.image ? '<img src="' + item.image + '" alt="" loading="lazy">' : '') +
        '</a>' +
        '<div class="product-card__info">' +
        // Mirror the Liquid card markup: price (nested .price) ABOVE title
        '<div class="product-card__price"><div class="price"><span class="price__current">' + formatMoney(item.price) + '</span></div></div>' +
        '<h3 class="product-card__title"><a href="' + item.url + '">' + escapeHtml(item.title) + '</a></h3>' +
        '</div></div>'
      );
    }).join('');

    container.querySelector('[data-recently-viewed-grid]').innerHTML = html;
    container.hidden = false;
  }

  /* ---------- Product form: variant handling ---------- */

  function initProductForms() {
    document.querySelectorAll('[data-product-form-wrapper]').forEach(function (wrapper) {
      var jsonEl = wrapper.querySelector('[data-variants-json]');
      if (!jsonEl) return;
      var variants;
      try {
        variants = JSON.parse(jsonEl.textContent);
      } catch (err) {
        return;
      }
      var idInput = wrapper.querySelector('[data-variant-id]');
      var button = wrapper.querySelector('[data-atc-button]');
      var priceEl = wrapper.querySelector('[data-product-price]');
      var invEl = wrapper.querySelector('[data-inventory-status]');
      // Shopify omits inventory_quantity from the variants JSON, so quantities
      // come from a separate Liquid-rendered map of variant id -> quantity.
      var inventoryMap = {};
      var invJsonEl = wrapper.querySelector('[data-inventory-json]');
      if (invJsonEl) {
        try {
          inventoryMap = JSON.parse(invJsonEl.textContent) || {};
        } catch (err) {
          inventoryMap = {};
        }
      }

      function selectedOptions() {
        var options = [];
        wrapper.querySelectorAll('[data-option-index]').forEach(function (group) {
          var idx = parseInt(group.getAttribute('data-option-index'), 10);
          var checked = group.querySelector('input[type="radio"]:checked');
          var select = group.querySelector('select');
          options[idx] = checked ? checked.value : (select ? select.value : null);
        });
        return options;
      }

      function findVariant(options) {
        return variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === options[i]; });
        });
      }

      function updateUrl(variant) {
        if (!variant || !window.history.replaceState) return;
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      function onChange() {
        var variant = findVariant(selectedOptions());
        if (!variant) {
          if (button) {
            button.disabled = true;
            button.textContent = theme.strings.unavailable || 'Unavailable';
          }
          return;
        }
        if (idInput) idInput.value = variant.id;
        updateUrl(variant);
        if (priceEl) {
          var priceHtml = '<span class="price__current">' + formatMoney(variant.price) + '</span>';
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceHtml = '<span class="price__current">' + formatMoney(variant.price) + '</span>' +
              '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
            priceEl.classList.add('price--on-sale');
          } else {
            priceEl.classList.remove('price--on-sale');
          }
          priceEl.innerHTML = priceHtml;
        }
        if (button) {
          if (variant.available) {
            button.disabled = false;
            button.textContent = theme.strings.addToCart || 'Add to cart';
          } else {
            button.disabled = true;
            button.textContent = theme.strings.soldOut || 'Sold out';
          }
        }
        if (invEl) {
          var mode = invEl.getAttribute('data-inventory-display') || 'low_stock_only';
          var threshold = parseInt(invEl.getAttribute('data-inventory-threshold') || '10', 10);
          var qty = inventoryMap[variant.id];
          var managed = variant.inventory_management != null && variant.inventory_management !== '';
          invEl.hidden = true;
          if (variant.available && managed && typeof qty === 'number') {
            if (qty > 0 && qty <= threshold) {
              invEl.textContent = (theme.strings.lowStock || 'Only 999999 left in stock').replace('999999', qty);
              invEl.classList.add('inventory--low');
              invEl.hidden = false;
            } else if (mode === 'always' && qty > threshold) {
              invEl.textContent = theme.strings.inStock || 'In stock';
              invEl.classList.remove('inventory--low');
              invEl.hidden = false;
            }
          }
        }
        var media = document.querySelector('[data-media-id="' + (variant.featured_media && variant.featured_media.id) + '"]');
        if (media) {
          var gallery = media.closest('[data-product-gallery]');
          if (gallery) {
            gallery.querySelectorAll('[data-media-id]').forEach(function (m) { m.classList.remove('is-active'); });
            media.classList.add('is-active');
            var thumb = document.querySelector('[data-thumb-media-id="' + variant.featured_media.id + '"]');
            if (thumb) {
              document.querySelectorAll('[data-thumb-media-id]').forEach(function (t) { t.classList.remove('is-active'); });
              thumb.classList.add('is-active');
            }
          }
        }
      }

      wrapper.addEventListener('change', function (e) {
        if (e.target instanceof Element && e.target.closest('[data-option-index]')) onChange();
      });
    });
  }

  /* ---------- Product gallery thumbnails ---------- */

  function initGalleries() {
    document.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;
      var thumb = e.target.closest('[data-thumb-media-id]');
      if (!thumb) return;
      e.preventDefault();
      var id = thumb.getAttribute('data-thumb-media-id');
      var gallery = document.querySelector('[data-product-gallery]');
      if (!gallery) return;
      gallery.querySelectorAll('[data-media-id]').forEach(function (m) {
        m.classList.toggle('is-active', m.getAttribute('data-media-id') === id);
      });
      document.querySelectorAll('[data-thumb-media-id]').forEach(function (t) {
        t.classList.toggle('is-active', t === thumb);
      });
    });
  }

  /* ---------- Collection filters/sort auto-submit ---------- */

  function initCollectionControls() {
    document.querySelectorAll('[data-sort-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
    document.querySelectorAll('[data-filter-form]').forEach(function (form) {
      form.addEventListener('change', debounce(function () {
        form.submit();
      }, 350));
    });
  }

  /* ---------- Init ---------- */

  function init() {
    initStickyHeader();
    initSearchSlideout();
    initPredictiveSearch();
    initMobileNav();
    initDesktopNav();
    initQuantity();
    initCartDrawer();
    initAjaxAtc();
    initBackToTop();
    initCarousels();
    initProductForms();
    initGalleries();
    initCollectionControls();
    trackRecentlyViewed();
    renderRecentlyViewed();
    refreshCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: sections re-render with fresh markup, so re-bind
     carousel arrows inside the reloaded section container. A header
     re-render also replaces the search input + suggestions panel, so
     re-run predictive search against the new nodes (its listeners live
     on the input, not document, so re-running never double-binds; the
     nav/drawer/slideout handlers are document-delegated and survive). */
  document.addEventListener('shopify:section:load', function (event) {
    initCarousels(event.target);
    if (event.target.querySelector('[data-site-header]')) {
      initPredictiveSearch();
    }
  });
})();
